import { Button, IconButton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import { useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { cloneDeep } from 'lodash-es';
import { IForm } from '../../../view-models/form.view-model';
import { uuid } from '../../../util/uuid';
import { IItemWithId, SortableList } from '../../../components/SortableList/SortableList';
import { BasicDragHandle, SortableItem } from '../../../components/SortableList/SortableItem';
import { ConfigItemRow } from './ConfigItemRow';
import { ConfigPageContentsGrid } from './ConfigPageContentsGrid';
import { DataWithId } from './UseConfigActions';

export type ConfigViewMode = 'cards' | 'grid';

const MainWrapper = styled('div')`
  display: grid;
  grid-template-rows: min-content 1fr;
  padding: 8px 12px 12px;
  width: 100%;
`;

const ButtonsWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
  align-items: center;
`;

const Header = styled('div')`
  display: grid;
  grid-template-columns: 1fr max-content;
  margin-bottom: 1rem;
`;

const TitleWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.25rem;
`;

const Rows = styled('div')`
  display: flex;
  flex-direction: column;
  row-gap: 0.5rem;
`;

export interface IConfigPageProps<T extends FieldValues & DataWithId> {
  createBtnLabel: string;
  customToolbarElem?: React.JSX.Element;
  subHeaderElem?: React.JSX.Element;
  defaultViewMode?: ConfigViewMode;
  onCreate: () => Promise<void> | void;
  onDelete?: (item: T) => Promise<void> | void;
  onEdit: (item: T) => Promise<void> | void;
  onReorder?: (items: T[]) => Promise<void> | void;
  forms: IForm<T>[];
  title: string;
  showCreateButton?: boolean;
  /** When set, a back chevron is rendered on the left of the title. */
  onBack?: () => void;
}

export function ConfigPageContents<T extends FieldValues & DataWithId>(props: IConfigPageProps<T>) {
  const { createBtnLabel, defaultViewMode = 'cards', onCreate, title, showCreateButton = true } = props;
  const [viewMode, setViewMode] = useState<ConfigViewMode>(defaultViewMode);

  return (
    <MainWrapper>
      <Header>
        <div>
          <TitleWrapper>
            {props.onBack && (
              <IconButton aria-label='Back' size='small' edge='start' onClick={props.onBack}>
                <ChevronLeftIcon />
              </IconButton>
            )}
            <Typography variant={'h4'}>{title}</Typography>
          </TitleWrapper>
          {props.subHeaderElem}
        </div>
        <ButtonsWrapper>
          <ToggleButtonGroup
            exclusive
            size='small'
            color='secondary'
            value={viewMode}
            onChange={(_, next: ConfigViewMode | null) => next && setViewMode(next)}
            aria-label='View mode'
          >
            <ToggleButton value='cards' aria-label='Cards view'>
              <ViewAgendaIcon fontSize='small' />
            </ToggleButton>
            <ToggleButton value='grid' aria-label='Grid view'>
              <GridViewIcon fontSize='small' />
            </ToggleButton>
          </ToggleButtonGroup>
          {props.customToolbarElem}
          {showCreateButton && (
            <Button color={'secondary'} onClick={onCreate} startIcon={<AddIcon />} variant={'contained'}>
              {createBtnLabel}
            </Button>
          )}
        </ButtonsWrapper>
      </Header>
      {viewMode === 'grid' ? (
        <ConfigPageContentsGrid forms={props.forms} onEdit={props.onEdit} onDelete={props.onDelete} />
      ) : (
        <ItemRows {...props} />
      )}
    </MainWrapper>
  );
}

function ItemRows<T extends FieldValues & DataWithId>(props: IConfigPageProps<T>) {
  const { forms, onDelete, onEdit, onReorder } = props;

  if (props.onReorder) {
    const formsWithId = forms.map((form) => {
      return {
        ...form,
        id: uuid(),
      };
    }) as (IForm<T> & IItemWithId)[];
    return (
      <SortableList
        items={formsWithId}
        onChange={(items) => {
          const reOrderedItems = items.map((formItem, index) => {
            const data = cloneDeep(formItem.data);
            return {
              ...data,
              sortOrder: index,
            };
          });

          onReorder?.(reOrderedItems as unknown as T[]);
        }}
        renderItem={(item) => (
          <SortableItem id={item.id}>
            <ConfigItemRow key={item.id} form={item} onDelete={onDelete} onEdit={onEdit} />
            <BasicDragHandle />
          </SortableItem>
        )}
      />
    );
  } else {
    return (
      <Rows>
        {forms.map((form, index) => {
          return <ConfigItemRow key={form.title ?? index} form={form} onDelete={onDelete} onEdit={onEdit} />;
        })}
      </Rows>
    );
  }
}
